import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { Container, ContainerItem, Item } from "@egalregal/shared";
import {
  catalogListSchema,
  type CatalogList,
  type ContainerItemResponse,
  type OwnCatalogEntry,
  type SharedCatalogEntry,
} from "../../shared/api/requests";
import {
  containerSearchableText,
  itemSearchableText,
  hashString,
  effectiveSearchText,
  pathLabelOf,
  SEARCHABLE_TEXT_VERSION,
  type SearchText,
} from "@egalregal/shared";
import { embeddingService } from "./embeddingService";
import { ensureKeywordIndex } from "./textFallback";
import { log } from "../../logger";

/**
 * Local search index (ADR-0022 + item-scan-and-search 07/10/11). Holds an
 * in-memory mirror `containerVectors` / `itemVectors` of each document's 768-dim
 * embedding, backed by IndexedDB (A2), plus a mirror of the accessible item
 * universe and every placement link (ticket 07). `initIndex` is called after
 * catalog load (eager, A1) and after any change (SSE, A7) to embed missing
 * docs, prune removed ones, and rebuild the keyword fallback index.
 */

/** A browser-local placement mirrored from the server (ADR-0026). */
export type MirrorPlacement = ContainerItem;

/**
 * The retired offline slice's record shape (`offline: true`). Only the boot
 * purge reads this flag; nothing writes it any more (placement-ledger 01).
 */
type LegacyOfflinePlacement = MirrorPlacement & { offline?: boolean };

interface CatalogDB extends DBSchema {
  containers: {
    key: string;
    value: Container;
  };
  /**
   * The last `GET /api/catalogs` list, one record under {@link CATALOGS_KEY}
   * (offline read cache 01). Hydrated on boot when the API is unreachable, so
   * an offline cold start still has entry names, labels and writability.
   */
  entries: {
    key: string;
    value: CatalogList;
  };
  embeddings: {
    key: string;
    value: {
      containerId: string;
      dims: number;
      vector: number[];
      /** Hash of the embedding input text, so a changed container is re-embedded (A7). */
      textHash: string;
      /** Version of searchable text granularity that produced this vector (forces re-embed on bump). */
      textVersion?: number;
    };
  };
  items: {
    key: string;
    value: Item;
  };
  itemEmbeddings: {
    key: string;
    value: {
      itemId: string;
      dims: number;
      vector: number[];
      /** Hash of the embedding input text, so a changed item is re-embedded. */
      textHash: string;
      /** Version of searchable text granularity that produced this vector (forces re-embed on bump). */
      textVersion?: number;
    };
  };
  placements: {
    key: string;
    value: MirrorPlacement;
  };
}

let dbPromise: Promise<IDBPDatabase<CatalogDB>> | null = null;

/** The one record key of the cached catalog list (offline read cache 01). */
const CATALOGS_KEY = "catalogs";

/** The mirror's key (`itemId:containerId`); the ledger's optimistic key is reversed. */
function mirrorPlacementKey(itemId: string, containerId: string): string {
  return `${itemId}:${containerId}`;
}

function open(): Promise<IDBPDatabase<CatalogDB>> {
  if (!dbPromise) {
    dbPromise = openDB<CatalogDB>("segalerek-catalog", 3, {
      upgrade(db) {
        // v1/v2 stores are created if missing and otherwise left untouched, so
        // the existing container catalog, embeddings and item mirror survive
        // the bump.
        if (!db.objectStoreNames.contains("containers")) db.createObjectStore("containers", { keyPath: "id" });
        if (!db.objectStoreNames.contains("embeddings")) db.createObjectStore("embeddings", { keyPath: "containerId" });
        if (!db.objectStoreNames.contains("items")) db.createObjectStore("items", { keyPath: "id" });
        if (!db.objectStoreNames.contains("itemEmbeddings")) db.createObjectStore("itemEmbeddings", { keyPath: "itemId" });
        if (!db.objectStoreNames.contains("placements")) db.createObjectStore("placements");
        // v3 (offline read cache 01): the catalog entry list, one record.
        if (!db.objectStoreNames.contains("entries")) db.createObjectStore("entries");
      },
    });
  }
  return dbPromise;
}

/** In-memory mirror of the catalog embeddings, keyed by container id (for the router). */
export const containerVectors = new Map<string, Float32Array>();

/** In-memory mirror of each container's embedding input-text hash (for A7 diffing). */
const containerTextHashes = new Map<string, string>();
/** In-memory mirror of each container's embedding text version. */
const containerTextVersions = new Map<string, number>();

/** In-memory mirror of the accessible item universe (ticket 07). */
export const mirrorItems = new Map<string, Item>();

/** In-memory mirror of every placement link, keyed `itemId:containerId` (ticket 07). */
export const mirrorPlacements = new Map<string, MirrorPlacement>();

/** In-memory mirror of item embeddings (ticket 10). */
export const itemVectors = new Map<string, Float32Array>();

/** In-memory mirror of each item's embedding input-text hash (ticket 10 diffing). */
const itemTextHashes = new Map<string, string>();
/** In-memory mirror of each item's embedding text version. */
const itemTextVersions = new Map<string, number>();

async function saveEmbedding(containerId: string, vector: Float32Array, textHash: string): Promise<void> {
  const db = await open();
  await db.put("embeddings", {
    containerId,
    dims: vector.length,
    vector: Array.from(vector),
    textHash,
    textVersion: SEARCHABLE_TEXT_VERSION,
  });
}

async function loadEmbeddings(): Promise<void> {
  const db = await open();
  const all = await db.getAll("embeddings");
  containerVectors.clear();
  containerTextHashes.clear();
  containerTextVersions.clear();
  for (const e of all) {
    containerVectors.set(e.containerId, new Float32Array(e.vector));
    containerTextHashes.set(e.containerId, e.textHash);
    if (e.textVersion !== undefined) {
      containerTextVersions.set(e.containerId, e.textVersion);
    }
  }
}

async function pruneEmbeddings(activeIds: Set<string>): Promise<void> {
  const db = await open();
  const tx = db.transaction("embeddings", "readwrite");
  const all = await tx.store.getAllKeys();
  for (const key of all) {
    if (!activeIds.has(key)) {
      void tx.store.delete(key);
      containerVectors.delete(key);
      containerTextHashes.delete(key);
      containerTextVersions.delete(key);
    }
  }
  await tx.done;
}

/**
 * Replace the cached container snapshot in IndexedDB: upsert every readable
 * container and delete documents no longer in the union, so a revoked or
 * deleted entry cannot leave stale rows behind (ticket 12).
 */
export async function persistContainers(containers: Container[]): Promise<void> {
  const db = await open();
  const tx = db.transaction("containers", "readwrite");
  const current = await tx.store.getAllKeys();
  const incoming = new Set(containers.map((c) => c.id));
  for (const c of containers) {
    void tx.store.put(c);
  }
  for (const key of current) {
    if (!incoming.has(key)) void tx.store.delete(key);
  }
  await tx.done;
}

/**
 * The container snapshot persisted by an earlier index build, so search can
 * answer from the cached readable union before (or without) a network fetch
 * (ticket 12).
 */
export async function readCachedContainers(): Promise<Container[]> {
  const db = await open();
  return db.getAll("containers");
}

/**
 * Replace the one cached catalog list record with a successful load's result
 * (offline read cache 01). The write is best-effort: a cache failure must never
 * fail the catalog load itself, and a failed load never writes, so the previous
 * record stays intact.
 *
 * @param list the `entries` + `sharedWithMe` payload from GET /api/catalogs.
 */
export async function persistCatalogs(list: CatalogList): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  try {
    const db = await open();
    await db.put("entries", list, CATALOGS_KEY);
  } catch (err) {
    log.warn({ err }, "[catalog] Persisting the catalog entry list failed");
  }
}

/**
 * The catalog list persisted by the last successful load, or null when there is
 * none or the record is corrupt — the app then shows its empty catalog exactly
 * as it does today (offline read cache 01). Never throws.
 */
export async function cachedCatalogs(): Promise<{
  entries: OwnCatalogEntry[];
  sharedWithMe: SharedCatalogEntry[];
} | null> {
  if (typeof indexedDB === "undefined") return null;
  try {
    const db = await open();
    const record: unknown = await db.get("entries", CATALOGS_KEY);
    const parsed = catalogListSchema.safeParse(record);
    return parsed.success ? parsed.data : null;
  } catch (err) {
    log.warn({ err }, "[catalog] Cached catalog entry list unavailable");
    return null;
  }
}

/**
 * Drop every cached read model, in memory and in IndexedDB, in one
 * transaction (catalog-entry-sharing review, offline read cache 02). A cache
 * written by the previous session identity must never be rehydrated for the
 * next user — offline it would otherwise surface an ex-user's containers,
 * items, placements or entry names indefinitely. The in-memory mirrors are
 * emptied even without IndexedDB, and a failed transaction is logged rather
 * than thrown into the auth flow that calls this.
 */
export async function clearCatalogCache(): Promise<void> {
  containerVectors.clear();
  containerTextHashes.clear();
  containerTextVersions.clear();
  mirrorItems.clear();
  mirrorPlacements.clear();
  itemVectors.clear();
  itemTextHashes.clear();
  itemTextVersions.clear();
  if (typeof indexedDB === "undefined") return;
  try {
    const db = await open();
    const tx = db.transaction(
      ["containers", "embeddings", "items", "itemEmbeddings", "placements", "entries"],
      "readwrite",
    );
    await Promise.all([
      tx.objectStore("containers").clear(),
      tx.objectStore("embeddings").clear(),
      tx.objectStore("items").clear(),
      tx.objectStore("itemEmbeddings").clear(),
      tx.objectStore("placements").clear(),
      tx.objectStore("entries").clear(),
    ]);
    await tx.done;
  } catch (err) {
    log.warn({ err }, "[catalog] Clearing the cached read models failed");
  }
}

/** True for a placement record written by the retired offline slice. */
function isLegacyOfflinePlacement(placement: MirrorPlacement): boolean {
  return (placement as LegacyOfflinePlacement).offline === true;
}

/** Load the item + placement mirrors into memory from IndexedDB. */
async function loadMirror(): Promise<void> {
  const db = await open();
  const [items, storedPlacements, itemEmbeds] = await Promise.all([
    db.getAll("items"),
    db.getAll("placements"),
    db.getAll("itemEmbeddings"),
  ]);
  // Boot purge (placement-ledger 01): the retired offline slice wrote rows
  // flagged `offline: true` that nothing ever uploaded. Delete them before
  // they can enter the mirror, so they never resurface in lists or search.
  // The flag is gone from the live type; only this migration reads it.
  const placements = storedPlacements.filter((p) => !isLegacyOfflinePlacement(p));
  if (placements.length !== storedPlacements.length) {
    const tx = db.transaction("placements", "readwrite");
    for (const p of storedPlacements) {
      if (isLegacyOfflinePlacement(p)) {
        void tx.store.delete(mirrorPlacementKey(p.itemId, p.containerId));
      }
    }
    await tx.done;
  }
  mirrorItems.clear();
  mirrorPlacements.clear();
  itemVectors.clear();
  itemTextHashes.clear();
  itemTextVersions.clear();
  for (const item of items) mirrorItems.set(item.id, item);
  for (const p of placements) mirrorPlacements.set(mirrorPlacementKey(p.itemId, p.containerId), p);
  for (const e of itemEmbeds) {
    itemVectors.set(e.itemId, new Float32Array(e.vector));
    itemTextHashes.set(e.itemId, e.textHash);
    if (e.textVersion !== undefined) {
      itemTextVersions.set(e.itemId, e.textVersion);
    }
  }
}

/**
 * Apply a server-side sync snapshot (GET /api/items/sync) to the browser
 * mirror: items and placements are replaced wholesale. A placement the server
 * no longer reports is deleted, whatever flags an older build stored on it.
 */
async function syncMirror(items: Item[], placements: ContainerItem[]): Promise<void> {
  const db = await open();

  const itemTx = db.transaction("items", "readwrite");
  const currentItems = await itemTx.store.getAllKeys();
  for (const item of items) void itemTx.store.put(item);
  for (const key of currentItems) {
    if (!items.some((i) => i.id === key)) void itemTx.store.delete(key);
  }
  await itemTx.done;

  const placementTx = db.transaction("placements", "readwrite");
  const currentPlacements = await placementTx.store.getAll();
  const incoming = new Set(placements.map((p) => mirrorPlacementKey(p.itemId, p.containerId)));
  for (const p of placements) void placementTx.store.put({ ...p }, mirrorPlacementKey(p.itemId, p.containerId));
  for (const existing of currentPlacements) {
    if (!incoming.has(mirrorPlacementKey(existing.itemId, existing.containerId))) {
      void placementTx.store.delete(mirrorPlacementKey(existing.itemId, existing.containerId));
    }
  }
  await placementTx.done;

  mirrorItems.clear();
  for (const item of items) mirrorItems.set(item.id, item);
  // Rebuild the mirror from the server snapshot alone: no local-only row may
  // outlive a sync the server does not confirm.
  mirrorPlacements.clear();
  for (const p of placements) mirrorPlacements.set(mirrorPlacementKey(p.itemId, p.containerId), p);
}

/**
 * Apply a confirmed absolute quantity to the mirror immediately, so the
 * scanner's next decode of the same barcode already offers `total + 1` and
 * search sees the new count. Persisting to IndexedDB is best-effort; the next
 * server snapshot is authoritative.
 *
 * @param containerId holding container (the placement's `containerId`).
 * @param item the mirrored item being adjusted — also enters `mirrorItems`.
 * @param quantity the confirmed absolute quantity for this placement.
 */
export function recordMirroredQuantity(containerId: string, item: Item, quantity: number): void {
  const key = mirrorPlacementKey(item.id, containerId);
  const record: MirrorPlacement = {
    containerId,
    itemId: item.id,
    quantity,
    // A count change does not move the placement's age; keep the original.
    addedAt: mirrorPlacements.get(key)?.addedAt ?? new Date().toISOString(),
  };
  mirrorItems.set(item.id, item);
  mirrorPlacements.set(key, record);
  // Persistence is best-effort: even environments without IndexedDB must not
  // turn a confirmed quantity change into a failed one.
  void Promise.resolve()
    .then(() => open())
    .then((db) => db.put("placements", record, key))
    .catch((err) => {
      log.warn(
        { err, itemId: item.id, containerId },
        "Failed to persist mirrored item quantity",
      );
    });
}

/**
 * Drop a placement after a confirmed removal, so search and the item list stop
 * offering it before the next server snapshot. IndexedDB persistence is
 * best-effort, like the quantity mirror.
 */
export function removeMirroredPlacement(itemId: string, containerId: string): void {
  const key = mirrorPlacementKey(itemId, containerId);
  mirrorPlacements.delete(key);
  void Promise.resolve()
    .then(() => open())
    .then((db) => db.delete("placements", key))
    .catch((err) => {
      log.warn({ err, itemId, containerId }, "Failed to persist placement removal");
    });
}

/** All mirrored items, for the search router (ticket 07). */
export function getMirroredItems(): Item[] {
  return [...mirrorItems.values()];
}

/**
 * All mirrored placements (the last server-confirmed snapshot), for the search
 * router (ticket 07).
 */
export function getMirroredPlacements(): MirrorPlacement[] {
  return [...mirrorPlacements.values()];
}

/**
 * The item list's row seam. The server snapshot is authoritative, so this
 * returns its rows unchanged — a mirror-only placement is never injected as a
 * placeholder (placement-ledger 01: the offline write path is gone).
 */
export function mergeMirrorRows(
  _containerId: string,
  rows: ContainerItemResponse[],
): ContainerItemResponse[] {
  return rows;
}

async function saveItemEmbedding(itemId: string, vector: Float32Array, textHash: string): Promise<void> {
  const db = await open();
  await db.put("itemEmbeddings", {
    itemId,
    dims: vector.length,
    vector: Array.from(vector),
    textHash,
    textVersion: SEARCHABLE_TEXT_VERSION,
  });
}

async function pruneItemEmbeddings(activeIds: Set<string>): Promise<void> {
  const db = await open();
  const tx = db.transaction("itemEmbeddings", "readwrite");
  const all = await tx.store.getAllKeys();
  for (const key of all) {
    if (!activeIds.has(key)) {
      void tx.store.delete(key);
      itemVectors.delete(key);
      itemTextHashes.delete(key);
      itemTextVersions.delete(key);
    }
  }
  await tx.done;
}

/**
 * The server-maintained `data.searchText` (ADR-0036) as the embedding input for
 * an entity, or undefined so callers fall back to live extraction. A `version`
 * mismatch (a granularity scheme change) makes the stored text unusable.
 * `textHash` is recomputed from the text, never trusted from the wire.
 */
function storedSearchText(
  entity: { data?: { searchText?: SearchText } },
): { text: string; textHash: string } | undefined {
  const text = effectiveSearchText(entity);
  return text === undefined ? undefined : { text, textHash: hashString(text) };
}

/** Embed every item that is missing an embedding or whose text changed (ticket 10). */
async function embedMissingItems(items: Item[]): Promise<void> {
  if (!(await embeddingService.ready)) return;
  const active = new Set<string>();
  for (const item of items) {
    active.add(item.id);
    const stored = storedSearchText(item);
    const text = stored?.text ?? itemSearchableText(item).text;
    const textHash = stored?.textHash ?? hashString(text);
    const storedVersion = itemTextVersions.get(item.id);
    if (itemVectors.has(item.id) && itemTextHashes.get(item.id) === textHash) {
      // Same rule as containers: hash equal keeps the vector, a pre-version
      // record is stamped and kept, a behind-version record re-embeds.
      if (storedVersion === undefined) {
        await stampItemTextVersion(item.id, textHash);
        continue;
      }
      if (storedVersion === SEARCHABLE_TEXT_VERSION) continue;
    }
    const vec = await embeddingService.embed(text);
    await saveItemEmbedding(item.id, vec, textHash);
    itemVectors.set(item.id, vec);
    itemTextHashes.set(item.id, textHash);
    itemTextVersions.set(item.id, SEARCHABLE_TEXT_VERSION);
    await idle();
  }
  await pruneItemEmbeddings(active);
}

/**
 * Stamp the current granularity version onto a stored embedding that was
 * accepted by hash but predates the version field (ticket 04). Hash equality
 * proves the vector's input text is byte-identical to what today's extractor
 * produces, so the vector is valid for the current scheme and stamping it is
 * truthful — and it closes the gap that would otherwise let a pre-version
 * record sleep through a future `SEARCHABLE_TEXT_VERSION` bump.
 */
async function stampContainerTextVersion(containerId: string, textHash: string): Promise<void> {
  const vector = containerVectors.get(containerId);
  if (!vector) return;
  await saveEmbedding(containerId, vector, textHash);
  containerTextVersions.set(containerId, SEARCHABLE_TEXT_VERSION);
}

/** Item twin of `stampContainerTextVersion` (same record shape, other store). */
async function stampItemTextVersion(itemId: string, textHash: string): Promise<void> {
  const vector = itemVectors.get(itemId);
  if (!vector) return;
  await saveItemEmbedding(itemId, vector, textHash);
  itemTextVersions.set(itemId, SEARCHABLE_TEXT_VERSION);
}

/**
 * Compute and persist embeddings for every container that is missing one, or
 * whose embedding input text has changed since it was last embedded (A7), and
 * prune vectors for containers that no longer exist. Uses `requestIdleCallback`
 * between batches so the main thread stays responsive (ADR-0022, B2).
 */
async function embedMissing(containers: Container[]): Promise<void> {
  if (!(await embeddingService.ready)) return;
  const active = new Set<string>();
  for (const c of containers) {
    if (c.deleted) continue;
    active.add(c.id);
    const stored = storedSearchText(c);
    const text = stored?.text ?? containerSearchableText(c, pathLabelOf(containers, c.id)).text;
    const textHash = stored?.textHash ?? hashString(text);
    const storedVersion = containerTextVersions.get(c.id);
    if (containerVectors.has(c.id) && containerTextHashes.get(c.id) === textHash) {
      // Hash equal: the text is unchanged. A record still missing the version
      // field gets stamped and kept (no re-embed); a record whose stored
      // version is current is skipped; one behind falls through and re-embeds.
      if (storedVersion === undefined) {
        await stampContainerTextVersion(c.id, textHash);
        continue;
      }
      if (storedVersion === SEARCHABLE_TEXT_VERSION) continue;
    }
    const vec = await embeddingService.embed(text);
    await saveEmbedding(c.id, vec, textHash);
    containerVectors.set(c.id, vec);
    containerTextHashes.set(c.id, textHash);
    containerTextVersions.set(c.id, SEARCHABLE_TEXT_VERSION);
    await idle();
  }
  await pruneEmbeddings(active);
}

function idle(): Promise<void> {
  return new Promise((resolve) => {
    const schedule = () => resolve();
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(schedule, { timeout: 500 });
    } else {
      setTimeout(schedule, 0);
    }
  });
}

/**
 * (Re)build the whole local index for a catalog + item-mirror snapshot.
 * Idempotent: loads stored embeddings/mirror on first call, embeds only what's
 * missing, prunes removed documents, persists the catalog and mirror, and
 * (re)builds the keyword fallback index. `items`/`placements` are the mirror
 * sync payload (ticket 07); when omitted, the stored mirror is reused as-is.
 * Both engines consume the same input string: the server-maintained
 * `data.searchText` when its version matches, else live extraction (ADR-0036).
 *
 * @param containers the catalog snapshot to index.
 * @param items optional mirror items; omitted reuses the stored mirror.
 * @param placements optional mirror placements; omitted reuses the stored mirror.
 * @param options `embed: false` keeps the build keyword-only, so a background
 *   catalog refresh never wakes the embedding model (shell-redesign 09, spec
 *   §5). Omitted keeps the historical embed-on-build behaviour.
 */
export async function initIndex(
  containers: Container[],
  items?: Item[],
  placements?: ContainerItem[],
  options: { embed?: boolean } = {},
): Promise<void> {
  const embed = options.embed ?? true;
  await Promise.all([loadEmbeddings(), loadMirror()]);
  const snapshotItems = items ?? [...mirrorItems.values()];
  const snapshotPlacements = placements ?? getMirroredPlacements();
  if (items || placements) {
    await syncMirror(snapshotItems, snapshotPlacements);
  }
  const embeddedItems = snapshotItems;
  // Keyword mode never wakes EmbeddingGemma (ADR-0022 B5): skip init and the
  // vector build entirely, but still (re)build the Orama keyword index below.
  // Pruning is pure bookkeeping, so stale vectors are swept in keyword mode too
  // (ticket 12) — without touching the model.
  if (embed) {
    await embedMissing(containers.filter((c) => !c.deleted));
    await embedMissingItems(embeddedItems);
  } else {
    await pruneEmbeddings(new Set(containers.filter((c) => !c.deleted).map((c) => c.id)));
    await pruneItemEmbeddings(new Set(embeddedItems.map((i) => i.id)));
  }
  await ensureKeywordIndex(containers, embeddedItems, snapshotPlacements);
  await persistContainers(containers);
}
